import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import './GraphicTryoutSplitScreen.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicTryoutSplitScreen({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam,
    ageGroup,
    tryoutDate,
    tryoutTime,
    location,
    contact,
    additionalInfo,
  } = graphicData;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || '#1a202c';

  const getBackgroundStyle = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(180deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(180deg, ${teamColors.primary} 0%, ${teamColors.primary} 50%, ${teamColors.secondary} 50%, ${teamColors.secondary} 100%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(180deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const isLightColor = (color) => {
    if (!color || color === 'null') return false; // Default to dark if color is null/undefined
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const getTextColor = (bgColor) => {
    return isLightColor(bgColor) ? '#1a202c' : '#ffffff';
  };

  return (
    <div className="graphic-tryout-split" id="graphic-tryout">
      {/* Left Side - Logo & Badge */}
      <div
        className="tryout-split-left"
        style={{ background: getBackgroundStyle() }}
      >
        <div
          className="tryout-split-badge-top"
          style={{
            background: accent,
            color: getTextColor(accent),
            fontFamily: heroFont,
            fontSize: heroFontSize * 0.13 + 'px',
          }}
        >
          TRYOUTS
        </div>

        <div className="tryout-split-logo-section">
          {homeTeamLogo ? (
            <div className="team-logo-circle team-logo-circle-lg">
              <img
                src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
                alt={homeTeam}
                className="tryout-split-logo"
              />
            </div>
          ) : (
            <div className="team-logo-circle team-logo-circle-lg">
              <div className="tryout-split-logo-placeholder">
                {teamSport === 'Softball' ? '🥎' : '⚾'}
              </div>
            </div>
          )}
        </div>

        <div
          className="tryout-split-team-info"
          style={{ color: getTextColor(teamColors.primary) }}
        >
          <div className="tryout-split-team-name">
            {homeTeam || 'TEAM NAME'}
          </div>
          {ageGroup && <div className="tryout-split-age-group">{ageGroup}</div>}
        </div>
      </div>

      {/* Right Side - Details */}
      <div className="tryout-split-right">
        <div className="tryout-split-details">
          {tryoutDate && (
            <div className="tryout-split-detail-item">
              <div className="tryout-split-detail-content">
                <div className="tryout-split-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>DATE</div>
                <div className="tryout-split-detail-value" style={{ color: detailColor }}>
                  {formatGameDate(tryoutDate, dateFormat)}
                </div>
              </div>
            </div>
          )}

          {tryoutTime && (
            <div className="tryout-split-detail-item">
              <div className="tryout-split-detail-content">
                <div className="tryout-split-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>TIME</div>
                <div className="tryout-split-detail-value" style={{ color: detailColor }}>
                  {formatGameTime(tryoutTime)}
                </div>
              </div>
            </div>
          )}

          {location && (
            <div className="tryout-split-detail-item">
              <div className="tryout-split-detail-content">
                <div className="tryout-split-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>LOCATION</div>
                <div className="tryout-split-detail-value" style={{ color: detailColor }}>{location}</div>
              </div>
            </div>
          )}

          {contact && (
            <div className="tryout-split-detail-item">
              <div className="tryout-split-detail-content">
                <div className="tryout-split-detail-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>CONTACT</div>
                <div className="tryout-split-detail-value" style={{ color: detailColor }}>{contact}</div>
              </div>
            </div>
          )}
        </div>

        {additionalInfo && (
          <div className="tryout-split-additional">
            <div className="tryout-split-additional-text">{additionalInfo}</div>
          </div>
        )}

        {showWatermark && (
          <div className="playmaker-watermark">
            <img src="/PMlogoleft.png" alt="Playmaker Media Kit" />
          </div>
        )}
      </div>
    </div>
  );
}

export default GraphicTryoutSplitScreen;
